export enum CollectionAppointmentType {
  HomeCollection = 1,
  InOfficeCollection = 2,
}

export enum CollectionAppointmentStatus {
  PendingEmailVerification = 1,
  PendingApproval = 2,
  Confirmed = 3,
  ChangeProposed = 4,
  EnRoute = 5,
  Arrived = 6,
  CollectionInProgress = 7,
  Completed = 8,
  Declined = 9,
  Cancelled = 10,
  NoShow = 11,
  Expired = 12,
}

export enum AppointmentRequesterRelationship {
  Self = 1,
  ParentOrGuardian = 2,
  Relative = 3,
  Caregiver = 4,
  OtherAuthorizedPerson = 5,
}

export enum AppointmentRequestedItemType {
  Test = 1,
  Panel = 2,
  DiscussAtVisit = 3,
}

export enum AppointmentAvailabilityExceptionType {
  Leave = 1,
  Blackout = 2,
  OneOffOpening = 3,
  ScheduleOverride = 4,
}

export enum AppointmentCompletionOutcome {
  Collected = 1,
  PartiallyCollected = 2,
  UnableToCollect = 3,
  PatientDeclined = 4,
  PatientUnavailable = 5,
  UnsafeEnvironment = 6,
  Other = 7,
}

export interface PublicBookingOfficeDto {
  id: string;
  name: string;
  city: string | null;
  administrativeArea: string | null;
  openingHours: string | null;
}

export interface PublicHomeServiceAreaDto {
  id: string;
  parish: string;
  name: string;
  currency: string;
  fee: number;
  taxRate: number;
  durationMinutes: number;
  travelBufferMinutes: number;
}

export interface PublicBookableCatalogItemDto {
  id: string;
  type: AppointmentRequestedItemType;
  code: string;
  name: string;
  category: string;
  description: string | null;
  price: number;
  taxRate: number;
  currency: string;
  requiresPhysicianOrder: boolean;
  isHomeCollectionBookable: boolean;
  orderableLocationIds: string[];
}

export interface PublicAppointmentConfigurationDto {
  enabled: boolean;
  minimumLeadTimeHours: number;
  maximumAdvanceDays: number;
  cancellationCutoffHours: number;
  timeZone: string;
  turnstileEnabled: boolean;
  turnstileSiteKey: string | null;
  offices: PublicBookingOfficeDto[];
  homeServiceAreas: PublicHomeServiceAreaDto[];
  catalog: PublicBookableCatalogItemDto[];
}

export interface PublicAppointmentSlotDto {
  id: string;
  startsAtUtc: string;
  endsAtUtc: string;
  localDate: string;
  localStartTime: string;
  localEndTime: string;
  timeZone: string;
}

export interface StaffAppointmentSlotDto extends PublicAppointmentSlotDto {
  staffUserId: string;
  staffName: string;
  homeServiceAreaId: string | null;
  officeLocationId: string | null;
}

export interface AppointmentEstimateItemInput {
  itemId: string;
  type: AppointmentRequestedItemType;
}

export interface AppointmentEstimateRequest {
  type: CollectionAppointmentType;
  homeServiceAreaId: string | null;
  officeLocationId: string | null;
  items: AppointmentEstimateItemInput[];
  discussTestsAtVisit: boolean;
}

export interface AppointmentEstimateLineDto {
  itemId: string | null;
  code: string;
  name: string;
  subtotal: number;
  tax: number;
  total: number;
}

export interface AppointmentEstimateDto {
  currency: string;
  testSubtotal: number;
  visitFee: number;
  tax: number;
  total: number;
  pricedAtUtc: string;
  lines: AppointmentEstimateLineDto[];
}

export interface PublicAppointmentPersonInput {
  firstName: string;
  lastName: string;
  email: string;
  phone: string | null;
}

export interface PublicAppointmentPatientInput {
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  sex: string | null;
  email: string | null;
  phone: string | null;
}

export interface CreatePublicAppointmentRequest {
  requestId: string;
  appointmentSlotId: string;
  type: CollectionAppointmentType;
  requesterRelationship: AppointmentRequesterRelationship;
  requester: PublicAppointmentPersonInput;
  patient: PublicAppointmentPatientInput;
  receiptEmail: string;
  proxyAuthorityAttested: boolean;
  homeServiceAreaId: string | null;
  officeLocationId: string | null;
  addressLine1: string | null;
  addressLine2: string | null;
  community: string | null;
  parish: string | null;
  accessDirections: string | null;
  clinicalNotes: string | null;
  items: AppointmentEstimateItemInput[];
  discussTestsAtVisit: boolean;
  turnstileToken: string | null;
}

export type CreateStaffCollectionAppointmentRequest = Omit<
  CreatePublicAppointmentRequest,
  "turnstileToken"
>;

export interface PublicAppointmentCreatedDto {
  requestId: string;
  appointmentNumber: string;
  status: CollectionAppointmentStatus;
  verificationExpiresAtUtc: string;
  maskedEmail: string;
}

export interface PublicAppointmentAccessDto {
  managementToken: string;
  appointmentNumber: string;
  status: CollectionAppointmentStatus;
  requestedStartUtc: string;
  confirmedStartUtc: string | null;
  managementTokenExpiresAtUtc: string;
}

export interface PublicAppointmentStatusDto {
  appointmentNumber: string;
  type: CollectionAppointmentType;
  status: CollectionAppointmentStatus;
  requestedStartUtc: string;
  confirmedStartUtc: string | null;
  confirmedEndUtc: string | null;
  timeZone: string;
  locationSummary: string;
  estimate: AppointmentEstimateDto;
  canCancel: boolean;
  canRequestChange: boolean;
  canAcceptProposedTime: boolean;
}

export interface AppointmentConfigurationDto {
  publicBookingEnabled: boolean;
  minimumLeadTimeHours: number;
  maximumAdvanceDays: number;
  unverifiedHoldMinutes: number;
  approvalExpiryHours: number;
  cancellationCutoffHours: number;
  defaultInOfficeDurationMinutes: number;
  defaultInOfficeBufferMinutes: number;
  verificationAttemptLimit: number;
  reminderHoursBefore: number;
  deploymentTimeZone: string;
  turnstileEnabled: boolean;
  concurrencyStamp: string;
}

export interface SaveAppointmentConfigurationRequest
  extends Omit<AppointmentConfigurationDto, "concurrencyStamp"> {
  concurrencyStamp: string | null;
}

export interface HomeServiceAreaRateDto {
  id: string;
  fee: number;
  taxRate: number;
  currency: string;
  durationMinutes: number;
  travelBufferMinutes: number;
  effectiveFrom: string;
  effectiveTo: string | null;
  isActive: boolean;
  concurrencyStamp: string;
}

export interface HomeServiceAreaDto {
  id: string;
  parish: string;
  name: string;
  code: string;
  isActive: boolean;
  isPublic: boolean;
  displayOrder: number;
  rates: HomeServiceAreaRateDto[];
  concurrencyStamp: string;
}

export interface SaveHomeServiceAreaRequest {
  parish: string;
  name: string;
  code: string;
  isActive: boolean;
  isPublic: boolean;
  displayOrder: number;
  concurrencyStamp: string | null;
}

export interface SaveHomeServiceAreaRateRequest {
  fee: number;
  taxRate: number;
  currency: string;
  durationMinutes: number;
  travelBufferMinutes: number;
  effectiveFrom: string;
  effectiveTo: string | null;
  concurrencyStamp: string | null;
}

export interface AppointmentAvailabilityRuleDto {
  id: string;
  name: string;
  staffUserId: string;
  staffName: string;
  appointmentType: CollectionAppointmentType;
  officeLocationId: string | null;
  homeServiceAreaId: string | null;
  dayOfWeek: number;
  startLocalTime: string;
  endLocalTime: string;
  validFrom: string;
  validTo: string | null;
  slotDurationMinutes: number;
  bufferBeforeMinutes: number;
  bufferAfterMinutes: number;
  isActive: boolean;
  concurrencyStamp: string;
}

export interface SaveAppointmentAvailabilityRuleRequest {
  name: string;
  staffUserId: string;
  appointmentType: CollectionAppointmentType;
  officeLocationId: string | null;
  homeServiceAreaId: string | null;
  dayOfWeek: number;
  startLocalTime: string;
  endLocalTime: string;
  validFrom: string;
  validTo: string | null;
  slotDurationMinutes: number;
  bufferBeforeMinutes: number;
  bufferAfterMinutes: number;
  isActive: boolean;
  concurrencyStamp: string | null;
}

export interface AppointmentAvailabilityExceptionDto {
  id: string;
  staffUserId: string;
  staffName: string;
  type: AppointmentAvailabilityExceptionType;
  appointmentType: CollectionAppointmentType | null;
  officeLocationId: string | null;
  homeServiceAreaId: string | null;
  slotDurationMinutes: number | null;
  startUtc: string;
  endUtc: string;
  reason: string;
  isActive: boolean;
  concurrencyStamp: string;
}

export interface CreateAppointmentAvailabilityExceptionRequest {
  staffUserId: string;
  type: AppointmentAvailabilityExceptionType;
  appointmentType: CollectionAppointmentType | null;
  officeLocationId: string | null;
  homeServiceAreaId: string | null;
  slotDurationMinutes: number | null;
  startUtc: string;
  endUtc: string;
  reason: string;
}

export interface AppointmentRequestedItemDto {
  id: string;
  type: AppointmentRequestedItemType;
  catalogItemId: string | null;
  panelId: string | null;
  code: string;
  name: string;
  subtotal: number;
  tax: number;
  total: number;
  requiresPhysicianOrder: boolean;
}

export interface CollectionAppointmentSummaryDto {
  id: string;
  appointmentNumber: string;
  type: CollectionAppointmentType;
  status: CollectionAppointmentStatus;
  patientName: string;
  requesterName: string;
  requesterRelationship: string;
  contact: string;
  locationSummary: string;
  homeServiceAreaId: string | null;
  officeLocationId: string | null;
  requestedStartUtc: string;
  confirmedStartUtc: string | null;
  assignedStaffId: string | null;
  assignedStaffName: string | null;
  currency: string;
  totalEstimate: number;
  requestedItemCount: number;
  concurrencyStamp: string;
}

export interface AppointmentStaffOptionDto {
  id: string;
  name: string;
  roles: string[];
}

export interface AppointmentOperationalFilterOptionsDto {
  staff: AppointmentStaffOptionDto[];
  homeServiceAreas: PublicHomeServiceAreaDto[];
  offices: PublicBookingOfficeDto[];
}

export interface CollectionAppointmentDetailDto
  extends Omit<
    CollectionAppointmentSummaryDto,
    "contact" | "requestedItemCount" | "requesterRelationship"
  > {
  requester: PublicAppointmentPersonInput;
  patient: PublicAppointmentPatientInput;
  requesterRelationship: AppointmentRequesterRelationship;
  receiptEmail: string;
  addressLine1: string | null;
  appointmentSlotId: string;
  homeServiceAreaId: string | null;
  officeLocationId: string | null;
  mobileServiceLocationId: string | null;
  patientId: string | null;
  addressLine2: string | null;
  community: string | null;
  parish: string | null;
  accessDirections: string | null;
  clinicalNotes: string | null;
  proxyAuthorityAttested: boolean;
  patientConsentConfirmed: boolean;
  confirmedEndUtc: string | null;
  deploymentTimeZone: string;
  testEstimateSubtotal: number;
  visitFeeSubtotal: number;
  taxEstimate: number;
  requestedItems: AppointmentRequestedItemDto[];
  linkedOrderIds: string[];
}

export interface AppointmentDecisionRequest {
  concurrencyStamp: string;
  assignedStaffId: string | null;
  alternateSlotId: string | null;
  reason: string | null;
}

export interface AppointmentProgressRequest {
  concurrencyStamp: string;
  status: CollectionAppointmentStatus;
  reason: string | null;
  patientConsentConfirmed: boolean;
  completionOutcome: AppointmentCompletionOutcome | null;
}

export type AppointmentPaymentMethod =
  | "Cash"
  | "Card"
  | "Cheque"
  | "BankTransfer"
  | "Other";

export interface AppointmentPaymentTenderDto {
  method: AppointmentPaymentMethod;
  amount: number;
  reference: string | null;
}

export interface AppointmentPaymentAllocationDto {
  orderId: string | null;
  appliesToVisitFee: boolean;
  amount: number;
}

export interface AppointmentPaymentDto {
  id: string;
  receiptNumber: string;
  type: "Payment" | "Refund";
  totalAmount: number;
  currency: string;
  occurredAt: string;
  operatingLocationId: string;
  operatingLocationName: string;
  recordedBy: string;
  relatedPaymentId: string | null;
  tenders: AppointmentPaymentTenderDto[];
  allocations: AppointmentPaymentAllocationDto[];
}

export interface AppointmentSettlementLineDto {
  orderId: string | null;
  code: string;
  description: string;
  subtotal: number;
  tax: number;
  total: number;
}

export interface AppointmentSettlementDto {
  appointmentId: string;
  appointmentNumber: string;
  currency: string;
  visitFeeSubtotal: number;
  visitFeeTax: number;
  visitFeeTotal: number;
  orderChargeTotal: number;
  paymentTotal: number;
  refundTotal: number;
  balance: number;
  lines: AppointmentSettlementLineDto[];
  payments: AppointmentPaymentDto[];
}

export interface RecordAppointmentPaymentRequest {
  concurrencyStamp: string;
  operatingLocationId: string | null;
  idempotencyKey: string;
  currency: string;
  tenders: AppointmentPaymentTenderDto[];
  notes: string | null;
}

export interface RefundAppointmentPaymentRequest {
  concurrencyStamp: string;
  idempotencyKey: string;
  amount: number;
  reason: string | null;
}
